// ─────────────────────────────────────────────────────────────────────────────
// PsychologyAnalytics
//
// Psychology tab rebuilt as a proper detector tab. Structure:
//   1. Detector cards (Fear Spike, Impulsiveness, Low Focus, Mental State,
//      Conviction Calibration) — same shell as Behavior/Context tabs
//   2. Supporting reference section: Mental State breakdown table +
//      Emotion-driven win rates (kept from original Psychology tab as
//      useful informational context)
//
// Fields used: fear_level, greed_level, focus_level, impulsiveness,
//              mental_state, conviction_level
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { Pill } from "../../components/ui/Pill";
import { C } from "../../lib/colors";
import { fmt$, tradeAmount } from "../../lib/format";
import { classifyTrade } from "../../lib/analytics";
import { useDetectors } from "../../hooks/useDetectors";
import { writeSnapshots, getTargetDescriptor, computeProgress } from "../../lib/detectorState";
import { useDetectorStates } from "../../hooks/useDetectorStates";
import { useDetectorSnapshotsMap, getSeries } from "../../hooks/useDetectorSnapshotsMap";
import { DetectorActionsMenu } from "./DetectorActionsMenu";
import { Sparkline } from "../../components/Sparkline";
import { useViewport } from "../../hooks/useViewport";
import { InsufficientStatePanel, EarlySignalBanner } from "../../components/DetectorProgress";

// ─── Shared helpers ───────────────────────────────────────────────────────────
const CONFIDENCE_COLOR = {
  strong:       C.green,
  moderate:     C.yellow,
  thin:         C.orange || "#ff9500",
  insufficient: C.muted,
};

function ConfidenceBadge({ level }) {
  const color = CONFIDENCE_COLOR[level] || C.muted;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12,
      letterSpacing: "0.06em", textTransform: "uppercase",
      background: color + "22", border: `1px solid ${color}55`, color,
    }}>
      {level}
    </span>
  );
}

const STATUS_COLOR = {
  acknowledged:  C.blue,
  working_on_it: C.green,
  dismissed:     C.muted,
};

function StatusPill({ status }) {
  if (!status || status === "active") return null;
  const color = STATUS_COLOR[status] || C.muted;
  const label = status === "working_on_it" ? "Working on it" :
                status === "acknowledged"  ? "Acknowledged"  : "Dismissed";
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 12,
      background: color + "22", border: `1px solid ${color}55`,
      color, fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

function fmtPct(v) {
  if (v == null || isNaN(v)) return "—";
  return `${(v * 100).toFixed(0)}%`;
}

function NeedsData({ msg }) {
  return (
    <div style={{ padding: "16px 0", color: C.muted, fontSize: 12, fontStyle: "italic" }}>
      {msg}
    </div>
  );
}

// ─── Sparkline section ─────────────────────────────────────────────────────────
function SparklineSection({ detector, snapshotsMap, sinceDays }) {
  const { isMobile } = useViewport();
  const descriptor = getTargetDescriptor(detector.id);
  if (!descriptor) return null;

  const series = getSeries(snapshotsMap, detector.id, descriptor.metric, sinceDays);
  if (!series || series.length < 2) {
    return (
      <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic", paddingTop: 8 }}>
        ≥2 days needed for trend
      </div>
    );
  }

  const latest = series[series.length - 1]?.value;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
        {descriptor.label} — last {sinceDays}d
      </div>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "flex-end",
        flexDirection: isMobile ? "column" : "row",
        gap: 12,
      }}>
        <Sparkline
          points={series}
          width={isMobile ? undefined : 220}
          height={48}
          direction={descriptor.direction}
          formatter={descriptor.formatter}
          ariaLabel={`${detector.title} sparkline`}
          style={isMobile ? { width: "100%" } : undefined}
        />
        {latest != null && (
          <div style={{ textAlign: isMobile ? "left" : "right", minWidth: 70 }}>
            <div style={{ fontSize: 10, color: C.muted }}>latest</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: "monospace" }}>
              {descriptor.formatter(latest)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Detector detail renderers ─────────────────────────────────────────────────

function TwoGroupDetails({ d, leftLabel, rightLabel, leftKeys, rightKeys }) {
  if (!d) return <NeedsData msg="Not enough data yet." />;
  const isLeaking = d.is_leaking;
  return (
    <div>
      <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <StatBlock
          label={leftLabel}
          rows={leftKeys}
          accentColor={leftKeys[1]?.[1] >= 0 ? C.green : C.red}
        />
        <StatBlock
          label={rightLabel}
          rows={rightKeys}
          accentColor={rightKeys[1]?.[1] >= 0 ? C.green : C.red}
        />
      </div>
      {isLeaking !== undefined && (
        <div style={{
          padding: "10px 14px",
          background: isLeaking ? C.red + "10" : C.green + "10",
          border: `1px solid ${isLeaking ? C.red : C.green}30`,
          borderRadius: 8,
          fontSize: 12,
          color: C.text,
          lineHeight: 1.6,
        }}>
          {isLeaking
            ? `Win rate gap: ${Math.abs(d.wr_gap_pp ?? 0).toFixed(0)}pp. This state is measurably costing you money.`
            : "No meaningful edge difference detected. Keep logging to build confidence."}
        </div>
      )}
    </div>
  );
}

function FearSpikeDetails({ d }) {
  if (!d?.n_fear) return <NeedsData msg="Log fear_level when entering trades." />;
  return (
    <TwoGroupDetails
      d={d}
      leftLabel={`High fear trades (fear ≥7, n=${d.n_fear})`}
      rightLabel={`Normal trades (fear <7, n=${d.n_normal})`}
      leftKeys={[["Win Rate", fmtPct(d.fear_wr)], ["Avg net P&L", fmt$(d.fear_avg_net)]]}
      rightKeys={[["Win Rate", fmtPct(d.normal_wr)], ["Avg net P&L", fmt$(d.normal_avg_net)]]}
    />
  );
}

function ImpulsivenessDetails({ d }) {
  if (!d?.n_impulse) return <NeedsData msg="Log impulsiveness when entering trades." />;
  return (
    <TwoGroupDetails
      d={d}
      leftLabel={`Impulsive trades (≥7, n=${d.n_impulse})`}
      rightLabel={`Disciplined trades (<7, n=${d.n_calm})`}
      leftKeys={[["Win Rate", fmtPct(d.impulse_wr)], ["Avg net P&L", fmt$(d.impulse_avg_net)]]}
      rightKeys={[["Win Rate", fmtPct(d.calm_wr)], ["Avg net P&L", fmt$(d.calm_avg_net)]]}
    />
  );
}

function LowFocusDetails({ d }) {
  if (!d?.n_low) return <NeedsData msg="Log focus_level when entering trades." />;
  return (
    <TwoGroupDetails
      d={d}
      leftLabel={`Low focus (≤4/10, n=${d.n_low})`}
      rightLabel={`Normal focus (>4/10, n=${d.n_normal})`}
      leftKeys={[["Win Rate", fmtPct(d.low_wr)], ["Avg net P&L", fmt$(d.low_avg_net)]]}
      rightKeys={[["Win Rate", fmtPct(d.normal_wr)], ["Avg net P&L", fmt$(d.normal_avg_net)]]}
    />
  );
}

function MentalStateDetails({ d }) {
  if (!d?.by_state?.length) return <NeedsData msg="Log mental_state when entering trades." />;
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
        {d.by_state.map(s => (
          <div key={s.state} style={{
            display: "grid",
            gridTemplateColumns: "130px 50px 1fr 90px",
            gap: 10, alignItems: "center",
            fontSize: 12, padding: "8px 10px",
            background: "#0b0d19", borderRadius: 6,
          }}>
            <span style={{
              color: s.avg_net >= 0 ? C.green : C.red,
              fontWeight: s === d.best || s === d.worst ? 700 : 400,
            }}>{s.state}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>n={s.n}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>WR {fmtPct(s.wr)}</span>
            <Pill value={s.avg_net} />
          </div>
        ))}
      </div>
      {d.is_leaking && d.worst && (
        <div style={{
          padding: "10px 14px",
          background: C.red + "10",
          border: `1px solid ${C.red}30`,
          borderRadius: 8,
          fontSize: 12, color: C.text, lineHeight: 1.6,
        }}>
          Consider a rule: when you notice yourself in a <strong>{d.worst.state}</strong> state,
          reduce position size by 50% or sit out until the state passes.
        </div>
      )}
    </div>
  );
}

function ConvictionDetails({ d }) {
  if (!d?.high_conv) return <NeedsData msg="Log conviction_level when entering trades." />;
  const tiers = [
    { label: "High conviction (7-10)", data: d.high_conv },
    { label: "Medium conviction (5-6)", data: d.mid_conv },
    { label: "Low conviction (1-4)",   data: d.low_conv  },
  ].filter(t => t.data?.n > 0);

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
        {tiers.map(({ label, data }) => (
          <div key={label} style={{
            display: "grid",
            gridTemplateColumns: "1fr 50px 80px 90px",
            gap: 8, alignItems: "center",
            fontSize: 12, padding: "8px 10px",
            background: "#0b0d19", borderRadius: 6,
          }}>
            <span style={{ color: C.text }}>{label}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>n={data.n}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>
              {data.wr != null ? `WR ${fmtPct(data.wr)}` : ""}
            </span>
            {data.avg_net != null ? <Pill value={data.avg_net} /> : <span />}
          </div>
        ))}
      </div>
      <div style={{
        padding: "10px 14px",
        background: d.is_miscalibrated ? C.red + "10" : C.green + "10",
        border: `1px solid ${d.is_miscalibrated ? C.red : C.green}30`,
        borderRadius: 8,
        fontSize: 12, color: C.text, lineHeight: 1.6,
      }}>
        {d.is_monotonic === true
          ? "Your conviction is well-calibrated. High-conviction calls outperform lower-conviction ones — trust your gut on sizing."
          : d.is_miscalibrated
          ? "High-conviction calls underperform medium ones. Consider whether 'feeling good' about a trade is reliable — it may indicate overconfidence."
          : "Calibration is unclear. Keep logging conviction levels to build a statistically reliable picture."}
      </div>
    </div>
  );
}

function StatBlock({ label, rows, accentColor }) {
  return (
    <div style={{ padding: "12px 14px", background: "#0b0d19", borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        {label}
      </div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
          <span style={{ color: C.muted }}>{k}</span>
          <span style={{ fontFamily: "monospace", color: accentColor || C.text, fontWeight: 600 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

// ─── DetectorCard ──────────────────────────────────────────────────────────────
function DetectorCard({ detector, state, accountId, onStatusChange, snapshotsMap, sinceDays }) {
  const { isMobile } = useViewport();
  const [open, setOpen] = useState(false);

  const hasCost = detector.estimated_cost > 0;
  const hasData = detector.confidence !== "insufficient";
  const status  = state?.status || "active";
  const isDismissed = status === "dismissed";

  const descriptor = getTargetDescriptor(detector.id);
  const progress = status === "working_on_it" && descriptor && state
    ? computeProgress(state, detector) : null;

  return (
    <div style={{
      padding: "16px 20px",
      background: C.panel,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      opacity: isDismissed ? 0.45 : 1,
      marginBottom: 8,
    }}>
      <div style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{detector.title}</span>
            <ConfidenceBadge level={detector.confidence} />
            {status !== "active" && <StatusPill status={status} />}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{detector.subtitle}</div>
          {detector.confidence === "thin" && <EarlySignalBanner detector={detector} />}
          {hasData && (
            <div style={{ fontSize: 13, color: C.sub, fontStyle: "italic" }}>{detector.summary}</div>
          )}
        </div>

        <div style={{
          minWidth: isMobile ? 0 : 140,
          textAlign: isMobile ? "left" : "right",
          display: "flex", flexDirection: "column",
          alignItems: isMobile ? "flex-start" : "flex-end",
        }}>
          {hasCost && hasData && status !== "acknowledged" && status !== "dismissed" && (
            <>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Est. Cost / Month
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.red, fontFamily: "monospace" }}>
                {fmt$(-detector.estimated_cost)}
              </div>
            </>
          )}
          {!hasCost && hasData && (
            <div style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>Informational</div>
          )}
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>n = {detector.n}</div>
        </div>

        <DetectorActionsMenu
          detector={detector}
          state={state}
          accountId={accountId}
          onChange={onStatusChange}
        />
      </div>

      {/* Insufficient-data state: progress bar + what's coming. Replaces the
          old "n = X / Y needed" stub so trial/new users see momentum. */}
      {!hasData && <InsufficientStatePanel detector={detector} />}

      {progress != null && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted, marginBottom: 4 }}>
            <span>Progress toward target</span>
            <span style={{ fontFamily: "monospace" }}>{Math.round(progress * 100)}%</span>
          </div>
          <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${Math.min(100, Math.max(0, progress * 100))}%`,
              background: progress >= 1 ? C.green : C.blue,
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>
      )}

      {hasData && (
        <SparklineSection detector={detector} snapshotsMap={snapshotsMap} sinceDays={sinceDays} />
      )}

      {hasData && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setOpen(o => !o)}
            style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 12, padding: 0, fontWeight: 600 }}
          >
            {open ? "▼ Hide details" : "▶ Show details"}
          </button>
          {open && (
            <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              {detector.id === "fear_spike"          && <FearSpikeDetails d={detector.details} />}
              {detector.id === "impulsiveness"       && <ImpulsivenessDetails d={detector.details} />}
              {detector.id === "low_focus"           && <LowFocusDetails d={detector.details} />}
              {detector.id === "mental_state"        && <MentalStateDetails d={detector.details} />}
              {detector.id === "conviction_mismatch" && <ConvictionDetails d={detector.details} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reference section: Mental State breakdown table ──────────────────────────
function MentalStateReference({ trades, pnlMode }) {
  const byState = Object.entries(
    trades.reduce((acc, t) => {
      const state = t.mental_state || "Unknown";
      if (!acc[state]) acc[state] = { pnl: 0, wins: 0, losses: 0, total: 0 };
      acc[state].pnl += tradeAmount(t, pnlMode);
      acc[state].total++;
      const k = classifyTrade(t);
      if (k === "win") acc[state].wins++;
      else if (k === "loss") acc[state].losses++;
      return acc;
    }, {})
  ).sort((a, b) => b[1].total - a[1].total);

  if (!byState.length) return null;

  return (
    <Card style={{ marginTop: 8 }}>
      <SectionTitle>Mental State Breakdown</SectionTitle>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 14px" }}>
        Reference table — all logged trades by mental state.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {byState.map(([state, data]) => {
          const avgPnL   = data.total > 0 ? data.pnl / data.total : 0;
          const denom    = data.wins + data.losses;
          const wr       = denom > 0 ? Math.round((data.wins / denom) * 100) : null;
          const isHR     = ["FOMO", "Anxious", "Frustrated", "Overconfident"].includes(state);
          return (
            <div key={state} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 14px", background: "#1a1d2e", borderRadius: 10,
              border: isHR ? `1px solid ${C.red}40` : "none",
            }}>
              <div>
                <div style={{ fontWeight: 600, color: C.text }}>{state}</div>
                <div style={{ fontSize: 12, color: C.muted }}>
                  {data.total} trades • {wr != null ? `${wr}% WR` : "—"}
                  {data.total < 5 && " (small sample)"}
                  {isHR && <span style={{ color: C.red, marginLeft: 6 }}>⚠ high-risk state</span>}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: avgPnL >= 0 ? C.green : C.red, fontWeight: 700, fontSize: 15, fontFamily: "monospace" }}>
                  {avgPnL >= 0 ? "+" : ""}{fmt$(avgPnL)}
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>avg P&L</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Emotion win rate tiles ────────────────────────────────────────────────────
function EmotionTiles({ trades }) {
  const { isMobile } = useViewport();

  function wrSubset(subset) {
    const decisive = subset.filter(t => t.result === "Win" || t.result === "Loss");
    if (!decisive.length) return null;
    return decisive.filter(t => t.result === "Win").length / decisive.length;
  }

  const tiles = [
    { label: "High Fear (≥7)",  trades: trades.filter(t => (t.fear_level || 0) >= 7),  color: C.red },
    { label: "High Greed (≥7)", trades: trades.filter(t => (t.greed_level || 0) >= 7), color: C.yellow },
    { label: "High Focus (≥8)", trades: trades.filter(t => (t.focus_level || 0) >= 8), color: C.green },
  ];

  return (
    <Card style={{ marginTop: 8 }}>
      <SectionTitle>Emotion-Driven Win Rates</SectionTitle>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 14px" }}>
        Win rate under specific emotional spikes. Scratches and open trades excluded.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
        {tiles.map(({ label, trades: subset, color }) => {
          const wr = wrSubset(subset);
          return (
            <div key={label} style={{
              textAlign: "center", padding: "16px 8px",
              background: "#1a1d2e", borderRadius: 10,
            }}>
              <div style={{ color, fontSize: 12, marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: "monospace" }}>
                {wr != null ? `${Math.round(wr * 100)}%` : "—"}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                {subset.length} trades
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PsychologyAnalytics — main export
// ─────────────────────────────────────────────────────────────────────────────
export function PsychologyAnalytics({ trades = [], userId, accountId, pnlMode = "net" }) {
  const [sinceDays, setSinceDays] = useState(90);

  // Run detectors server-side
  const { data: psychResult, loading: detectorsLoading, error: detectorsError } = useDetectors({
    suite: "psychology",
    trades,
    userId,
  });

  // All hooks must run unconditionally on every render. Keep them at the top
  // of the component, BEFORE any early returns (Rules of Hooks).
  //
  // `detectors` is wrapped in useMemo so the empty-array fallback has a
  // stable identity across renders — without it, useEffect below would
  // re-fire every render because [] !== [] by reference.
  const detectors = useMemo(() => psychResult?.detectors ?? [], [psychResult]);

  const { resolve, setStatus } = useDetectorStates(userId);
  const { map: snapshotsMap } = useDetectorSnapshotsMap({ userId, accountId, sinceDays });

  useEffect(() => {
    if (!userId || !accountId || !detectors.length) return;
    writeSnapshots({ userId, accountId, detectors }).catch(e =>
      console.warn("[PsychologyAnalytics] snapshot write failed:", e)
    );
  }, [userId, accountId, detectors]);

  if (detectorsLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: "#8892a4", fontSize: 14 }}>
        <span style={{ marginRight: 10 }}>⚙️</span> Analyzing patterns…
      </div>
    );
  }

  if (detectorsError && detectorsError !== "subscription_required") {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#ff6b6b", fontSize: 13 }}>
        Error loading detectors: {detectorsError}
      </div>
    );
  }

  const handleStatusChange = async (args) => {
    await setStatus({ ...args, allowUserWide: false });
  };

  const headlineCost = detectors
    .filter(d => {
      const s = resolve(d.id, accountId)?.status;
      return s !== "acknowledged" && s !== "dismissed";
    })
    .reduce((s, d) => s + (d.estimated_cost || 0), 0);

  const hasAnyData = detectors.some(d => d.confidence !== "insufficient");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ marginBottom: 8 }}>
        <SectionTitle>Psychology Analytics</SectionTitle>
        <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>
          Measurable cost of psychological states on your trading outcomes.
          Based on fear, focus, impulsiveness, and mental state fields logged at trade time.
        </p>
      </div>

      {headlineCost > 0 && (
        <Card style={{
          padding: "14px 18px",
          background: "#1a0b0b",
          border: `1px solid ${C.red}40`,
          marginBottom: 8,
        }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
            Combined Estimated Cost
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 11, color: C.sub }}>Observed rate, extrapolated to a monthly figure</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.red, fontFamily: "monospace" }}>
              {fmt$(-headlineCost)} / month
            </div>
          </div>
        </Card>
      )}

      {hasAnyData && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {[30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setSinceDays(d)}
              style={{
                padding: "4px 12px", borderRadius: 6,
                border: `1px solid ${sinceDays === d ? C.blue : C.border}`,
                background: sinceDays === d ? C.blue + "22" : "transparent",
                color: sinceDays === d ? C.blue : C.muted,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      )}

      {/* Detector cards */}
      {detectors.map(detector => (
        <DetectorCard
          key={detector.id}
          detector={detector}
          state={resolve(detector.id, accountId)}
          accountId={accountId}
          onStatusChange={handleStatusChange}
          snapshotsMap={snapshotsMap}
          sinceDays={sinceDays}
        />
      ))}

      {!hasAnyData && (
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🧠</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>
            Not enough psychology data yet
          </div>
          <div style={{ fontSize: 13, color: C.muted, maxWidth: 400, margin: "0 auto", lineHeight: 1.6 }}>
            Log <strong>fear level</strong>, <strong>focus level</strong>, <strong>impulsiveness</strong>,
            and <strong>mental state</strong> in the pre-trade section. With 10+ tagged trades,
            these detectors reveal what your emotional states are actually costing you.
          </div>
        </Card>
      )}

      {/* Supporting reference data */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, paddingLeft: 2 }}>
          Reference Data
        </div>
        <EmotionTiles trades={trades} />
        <MentalStateReference trades={trades} pnlMode={pnlMode} />
      </div>
    </div>
  );
}
